import { TrashIcon, ExclamationTriangleIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';
import type { FormEvent, ChangeEvent } from 'react';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import React from 'react';
import { apiUrl, authFetch, authService, courseApi } from '../utils/api';

interface Course {
  id: string;
  name: string;
  grade: string;
  term: string;
  status: 'active' | 'inactive';
  price: number;
  modificationDate: string;
  description?: string;
  imageUrl?: string;
  isOpenToAll: boolean;
  isSuggested: boolean;
}

interface FormData {
  name: string;
  grade: string;
  term: string;
  status: 'active' | 'inactive';
  price: string;
  description: string;
  imageFile: File | null;
  isOpenToAll: boolean;
  isSuggested: boolean;
}

interface SortConfig {
  key: keyof Course;
  direction: 'asc' | 'desc';
}

const Courses = () => {
  const navigate = useNavigate();
  const [showAddForm, setShowAddForm] = useState(false);
  const [courses, setCourses] = useState<Course[]>([]);
  const [filters, setFilters] = useState({
    courseName: '',
    grade: '',
    active: '',
    term: '',
  });
  const [displayedCourses, setDisplayedCourses] = useState<Course[]>([]);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    grade: '',
    term: '',
    status: 'active',
    price: '',
    description: '',
    imageFile: null,
    isOpenToAll: false, // Default to false
    isSuggested: false, // Default to لا (No)
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [courseToDelete, setCourseToDelete] = useState<Course | null>(null);
  const [courseToToggle, setCourseToToggle] = useState<Course | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Fetch courses from API on mount
  const fetchCourses = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch(
        apiUrl(`Course/all?pageNumber=${currentPage}&pageSize=${itemsPerPage}${filters.courseName ? `&courseName=${encodeURIComponent(filters.courseName)}` : ''}${filters.grade ? `&grade=${encodeURIComponent(filters.grade)}` : ''}${filters.active ? `&active=${filters.active}` : ''}${filters.term ? `&term=${encodeURIComponent(filters.term)}` : ''}`),
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );
      if (!response.ok) throw new Error('Failed to fetch courses');
      const data = await response.json();
      
      if (data.success) {
        const courseData = data.data;
        setTotalItems(courseData.totalCount);
        setTotalPages(courseData.totalPages);
        
        // Map API response to our Course interface
        const formattedCourses: Course[] = courseData.data.map((course: any) => ({
        id: course.id,
        name: course.courseName,
        grade: course.grade,
        term: course.term,
        status: course.active ? 'active' : 'inactive',
        price: course.price,
        modificationDate: course.modificationDate,
        description: course.description || '',
        imageUrl: course.imagePath || '',
        isOpenToAll: course.isOpenToAll || false,
        isSuggested: course.isSuggested ?? false,
        }));
        
        setCourses(formattedCourses);
        setDisplayedCourses(formattedCourses);
      } else {
        throw new Error(data.message || 'Failed to fetch courses');
      }
    } catch (error) {
      setCourses([]);
      setDisplayedCourses([]);
      setError('Failed to fetch courses. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, [currentPage, filters.courseName, filters.grade, filters.active, filters.term]);

  // Sort function should only sort the current page's data
  const handleSort = (key: keyof Course) => {
    setSortConfig((prevSortConfig) => {
      const direction = prevSortConfig?.key === key && prevSortConfig.direction === 'asc' ? 'desc' : 'asc';
      return { key, direction };
    });
  };

  // Apply sorting to the current page of data
  const sortedCourses = useMemo(() => {
    if (!sortConfig) return displayedCourses;
    
    return [...displayedCourses].sort((a, b) => {
      const key = sortConfig.key;
      const direction = sortConfig.direction;
      
      // Use type assertion to avoid TypeScript errors
      const aValue = a[key] as string | number;
      const bValue = b[key] as string | number;
      
      if (aValue < bValue) {
        return direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [displayedCourses, sortConfig]);

  // No need for client-side pagination calculations since API handles pagination
  
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    // The useEffect will trigger a new API call with the updated page number
  };

  const handleSearch = () => {
    setCurrentPage(1); // Reset to first page when filtering
    // The useEffect will trigger a new API call with the updated filters
  };

  // Reset all filters
  const handleResetFilters = () => {
    setFilters({
      courseName: '',
      grade: '',
      active: '',
      term: '',
    });
    setCurrentPage(1); // Reset to first page
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData((prev) => ({
      ...prev,
      imageFile: file
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    try {
      const body = new FormData();
      body.append('courseName', formData.name);
      body.append('price', formData.price);
      body.append('term', formData.term);
      body.append('active', (formData.status === 'active').toString());
      body.append('grade', formData.grade);
      if (formData.description) body.append('description', formData.description);
      if (formData.imageFile) body.append('imageFile', formData.imageFile);
      body.append('isOpenToAll', formData.isOpenToAll.toString());
      body.append('isSuggested', formData.isSuggested.toString());

      const response = await courseApi.addCourseWithImage(body);
      
      if (response.success) {
        // Refresh the course list after successful addition
        setCurrentPage(1); // Go back to first page
        setShowAddForm(false);
        setFormData({
          name: '',
          grade: '',
          term: '',
          status: 'active',
          price: '',
          description: '',
          imageFile: null,
          isOpenToAll: false,
          isSuggested: false,
        });
        fetchCourses();
        // The useEffect will automatically refresh the course list
      } else {
        alert('Failed to add course: ' + response.message);
      }
    } catch (error) {
      console.error('Error adding course:', error);
      alert('Failed to add course. Please try again later.');
    }
  };

  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    
    try {
      const response = await authFetch(apiUrl(`Course/${id}`), {
        method: 'DELETE',
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        // If API call succeeds, update the local state
        setCourses(courses.filter((course: Course) => course.id !== id));
        setDisplayedCourses(displayedCourses.filter((course: Course) => course.id !== id));
        setCourseToDelete(null);
      } else {
        alert('Failed to delete course: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting course:', error);
      alert('Failed to delete course. Please try again later.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleStatus = async (course: Course) => {
    try {
      const newStatus = course.status === 'active' ? 'inactive' : 'active';
      
      const response = await authFetch(apiUrl(`Course/${course.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': '*/*',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          id: course.id,
          courseName: course.name,
          term: course.term,
          active: newStatus === 'active',
          price: course.price,
          grade: course.grade,
          description: course.description || '',
          imageUrl: course.imageUrl || '',
          isOpenToAll: course.isOpenToAll,
          isSuggested: course.isSuggested,
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // If successful, update the local state and close the modal
        setCourses(
          courses.map((c: Course) =>
            c.id === course.id
              ? {
                  ...c,
                  status: newStatus,
                }
              : c
          )
        );
        setDisplayedCourses(
          displayedCourses.map((c: Course) =>
            c.id === course.id
              ? {
                  ...c,
                  status: newStatus,
                }
              : c
          )
        );
        setCourseToToggle(null);
      } else {
        alert('Failed to update course status: ' + data.message);
      }
    } catch (error) {
      console.error('Error updating course status:', error);
      alert('Failed to update course status. Please try again later.');
    }
  };

  const handleCourseClick = async (courseId: string) => {
    try {
      // Fetch the course details from the API
      const response = await authFetch(apiUrl(`Course/${courseId}`), {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Store the course data in localStorage or sessionStorage for the details page
        sessionStorage.setItem('currentCourseData', JSON.stringify(data.data));
        // Navigate to the course details page
        navigate(`/courses/${courseId}`);
      } else {
        console.error('Failed to fetch course details:', data.message);
        alert('Could not load course details. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching course details:', error);
      alert('Could not load course details. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                الباقات التدريبية
              </h1>
              <p className="mt-2 text-gray-600">إدارة كتالوج الباقات</p>
            </div>
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm rounded-lg 
                         shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 
                         focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              إضافة دورة جديدة
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 transition-all duration-200 hover:shadow-xl">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1">
              <label htmlFor="name-filter" className="block text-sm font-medium text-gray-700">
                اسم الدورة
              </label>
              <input
                type="text"
                id="name-filter"
                value={filters.courseName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, courseName: e.target.value })}
                className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         transition-colors duration-200"
                placeholder="البحث في الباقات..."
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="grade-filter" className="block text-sm font-medium text-gray-700">
                المستوى الدراسي
              </label>
              <select
                id="grade-filter"
                value={filters.grade}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, grade: e.target.value })}
                className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         transition-colors duration-200"
              >
                <option value="">جميع المستويات</option>
                <option value="Secondary1">الصف الأول الثانوي</option>
                <option value="Secondary2">الصف الثاني الثانوي</option>
                <option value="Secondary3">الصف الثالث الثانوي</option>
              </select>
            </div>
            <div className="space-y-1">
              <label htmlFor="term-filter" className="block text-sm font-medium text-gray-700">
                الفصل الدراسي
              </label>
              <select
                id="term-filter"
                value={filters.term}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, term: e.target.value })}
                className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         transition-colors duration-200"
              >
                <option value="">جميع الفصول</option>
                <option value="First">الفصل الأول</option>
                <option value="Second">الفصل الثاني</option>
              </select>
            </div>
            <div className="space-y-1">
              <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700">
                الحالة
              </label>
              <select
                id="status-filter"
                value={filters.active}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, active: e.target.value })}
                className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         transition-colors duration-200"
              >
                <option value="">جميع الحالات</option>
                <option value="true">نشط</option>
                <option value="false">غير نشط</option>
              </select>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-4">
            <button
              onClick={handleSearch}
              className="inline-flex items-center px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm rounded-lg
                       shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2
                       focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              تطبيق التصفية
            </button>
            <button
              onClick={handleResetFilters}
              className="inline-flex items-center px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 text-sm transition-colors duration-200"
            >
              إعادة تعيين
            </button>
          </div>
        </div>

        {/* Courses Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-200 hover:shadow-xl">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    اسم الدورة
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    المستوى
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    الفصل
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    الحالة
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    السعر
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    كورس مقترح
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    الإجراءات
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-4">
                      <div className="flex items-center justify-center text-red-500">
                        <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {error}
                      </div>
                    </td>
                  </tr>
                ) : sortedCourses.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center text-gray-500">
                        <svg className="w-12 h-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <p className="text-lg font-medium">لم يتم العثور على دورات</p>
                        <p className="text-sm">حاول تعديل التصفية للعثور على ما تبحث عنه</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  sortedCourses.map((course) => (
                    <tr
                      key={course.id}
                      className="hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                      onClick={() => handleCourseClick(course.id)}
                    >
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{course.name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600">{course.grade}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600">{course.term}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                          ${course.status === 'active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'}`}
                        >
                          {course.status === 'active' ? 'نشط' : 'غير نشط'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 font-medium">{course.price} جنيه</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                          ${course.isSuggested 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-gray-100 text-gray-800'}`}
                        >
                          {course.isSuggested ? 'نعم' : 'لا'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end space-x-reverse space-x-2">
                          <button
                            onClick={(e: { stopPropagation: () => void }) => {
                              e.stopPropagation();
                              setCourseToToggle(course);
                            }}
                            className={`inline-flex items-center p-2 rounded-lg transition-colors duration-200 ${
                              course.status === 'active'
                                ? 'text-yellow-600 hover:text-yellow-900 hover:bg-yellow-50'
                                : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                            }`}
                            title={course.status === 'active' ? 'إخفاء الدورة' : 'إظهار الدورة'}
                          >
                            {course.status === 'active' ? (
                              <EyeSlashIcon className="h-5 w-5" />
                            ) : (
                              <EyeIcon className="h-5 w-5" />
                            )}
                          </button>
                          <button
                            onClick={(e: { stopPropagation: () => void }) => {
                              e.stopPropagation();
                              setCourseToDelete(course);
                            }}
                            className="inline-flex items-center p-2 text-red-600 hover:text-red-900 rounded-lg hover:bg-red-50
                                     transition-colors duration-200"
                            title="حذف الدورة"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {!loading && displayedCourses.length > 0 && (
          <div className="mt-6 flex flex-col sm:flex-row justify-between items-center bg-white rounded-2xl shadow-lg px-6 py-4">
            <div className="text-sm text-gray-700 mb-4 sm:mb-0">
              عرض <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> إلى{' '}
              <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalItems)}</span>{' '}
              من <span className="font-medium">{totalItems}</span> نتيجة
            </div>
            <div className="flex items-center space-x-reverse space-x-2">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium ${
                  currentPage === 1
                    ? 'text-gray-400 bg-gray-50 cursor-not-allowed'
                    : 'text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200'
                }`}
              >
                الأول
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page => {
                  return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                })
                .map((page, index, array) => {
                  const prevPage = array[index - 1];
                  if (index > 0 && prevPage && page - prevPage > 1) {
                    return [
                      <span key={`ellipsis-${prevPage}`} className="px-3 py-2">...</span>,
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium
                          ${currentPage === page
                            ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                            : 'text-gray-700 hover:bg-gray-50 transition-colors duration-200'
                          }`}
                      >
                        {page}
                      </button>
                    ];
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`relative inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium
                        ${currentPage === page
                          ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                          : 'text-gray-700 hover:bg-gray-50 transition-colors duration-200'
                        }`}
                    >
                      {page}
                    </button>
                  );
                })}
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className={`relative inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium ${
                  currentPage === totalPages
                    ? 'text-gray-400 bg-gray-50 cursor-not-allowed'
                    : 'text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200'
                }`}
              >
                الأخير
              </button>
            </div>
          </div>
        )}

        {/* Add Course Form Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl sm:text-2xl font-bold mb-6">إضافة دورة جديدة</h2>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      الاسم
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={handleInputChange}
                      name="name"
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                      placeholder="أدخل اسم الدورة"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      المستوى
                    </label>
                    <select
                      name="grade"
                      value={formData.grade}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      required
                    >
                      <option value="">اختر المستوى</option>
                      <option value="Secondary1">الصف الأول الثانوي</option>
                      <option value="Secondary2">الصف الثاني الثانوي</option>
                      <option value="Secondary3">الصف الثالث الثانوي</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      الفصل الدراسي
                    </label>
                    <select
                      value={formData.term}
                      onChange={handleInputChange}
                      name="term"
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    >
                      <option value="">اختر الفصل</option>
                      <option value="First">الفصل الأول</option>
                      <option value="Second">الفصل الثاني</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      الحالة
                    </label>
                    <select
                      value={formData.status}
                      onChange={handleInputChange}
                      name="status"
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    >
                      <option value="active">نشط</option>
                      <option value="inactive">غير نشط</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      السعر
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={handleInputChange}
                      name="price"
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                      placeholder="أدخل سعر الدورة"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      وصف الدورة
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={handleInputChange}
                      name="description"
                      className="w-full px-3 py-2 border rounded-lg"
                      rows={4}
                      required
                      placeholder="أدخل وصف الدورة"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      صورة الدورة
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      name="imageFile"
                      className="w-full px-3 py-2 border rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary/90"
                    />
                    {formData.imageFile && (
                      <p className="mt-1 text-xs text-gray-500">تم اختيار: {formData.imageFile.name}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                    وضع مؤقت للمحاضرات
                    </label>
                    <select
                      value={formData.isOpenToAll ? 'true' : 'false'}
                      onChange={handleInputChange}
                      name="isOpenToAll"
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="false">نعم</option>
                      <option value="true">لا</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      كورس مقترح
                    </label>
                    <select
                      value={formData.isSuggested ? 'true' : 'false'}
                      onChange={(e) => setFormData(prev => ({ ...prev, isSuggested: e.target.value === 'true' }))}
                      name="isSuggested"
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="false">لا</option>
                      <option value="true">نعم</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end gap-4 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                  >
                    إضافة الدورة
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {courseToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md border border-gray-200 transform transition-all">
              <div className="text-center mb-5">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                  <TrashIcon className="h-8 w-8 text-red-600" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-2">حذف الدورة</h3>
                <p className="text-gray-600">
                  هل أنت متأكد من حذف <span className="font-bold text-gray-900">{courseToDelete.name}</span>؟
                  لا يمكن التراجع عن هذا الإجراء.
                </p>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-all focus:outline-none focus:ring-2 focus:ring-gray-300"
                  onClick={() => setCourseToDelete(null)}
                  disabled={isDeleting}
                >
                  إلغاء
                </button>
                <button
                  className="px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 transition-all focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-70 flex items-center gap-2"
                  onClick={() => handleDelete(courseToDelete.id)}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      جاري الحذف...
                    </>
                  ) : (
                    <>
                      <TrashIcon className="h-4 w-4" />
                      حذف
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Status Change Confirmation Modal */}
        {courseToToggle && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md border border-gray-200 transform transition-all">
              <div className="text-center mb-5">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
                  <ExclamationTriangleIcon className="h-8 w-8 text-blue-600" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-2">
                  {courseToToggle.status === 'active' ? 'إخفاء الدورة' : 'إظهار الدورة'}
                </h3>
                <p className="text-gray-600">
                  هل أنت متأكد من أنك تريد {courseToToggle.status === 'active' ? 'إخفاء' : 'إظهار'} الدورة <span className="font-bold text-gray-900">{courseToToggle.name}</span>؟
                  {courseToToggle.status === 'active' ? ' هذا سيجعلها غير مرئية للطلاب.' : ' هذا سيجعلها مرئية للطلاب.'}
                </p>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-all focus:outline-none focus:ring-2 focus:ring-gray-300"
                  onClick={() => setCourseToToggle(null)}
                >
                  إلغاء
                </button>
                <button
                  className={`px-4 py-2 rounded-lg ${
                    courseToToggle.status === 'active'
                      ? 'bg-yellow-600 hover:bg-yellow-700'
                      : 'bg-green-600 hover:bg-green-700'
                  } text-white font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    courseToToggle.status === 'active'
                      ? 'focus:ring-yellow-500'
                      : 'focus:ring-green-500'
                  }`}
                  onClick={() => handleToggleStatus(courseToToggle)}
                >
                  {courseToToggle.status === 'active' ? 'إخفاء الدورة' : 'إظهار الدورة'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Courses;